"use client";

import { ArrowDown, FileText, ShieldCheck, Truck, Wallet } from "lucide-react";
import type { Cadeia, EtapaChave, Viagem } from "@/types";
import { fmt } from "@/lib/format";
import { ETAPA_NOME, STATUS_ETAPA, STATUS_VIAGEM } from "@/lib/status";
import { cn } from "@/lib/utils";
import { ChainStatus, StatusBadge, StatusIcon } from "@/components/status";
import { Card } from "./ui";

/** Cartão de viagem para listas. */
export function ViagemCard({ v, onClick }: { v: Viagem; onClick: () => void }) {
  return (
    <Card onClick={onClick} className="p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[15px] font-semibold tnum">Viagem {fmt.int(v.numero)}</span>
        <StatusBadge def={STATUS_VIAGEM[v.situacao]} />
      </div>
      <p className="mt-0.5 truncate text-sm text-muted">{v.tomadorNome || "—"}</p>
      <div className="mt-3 flex items-stretch gap-3">
        <div className="flex w-3 flex-col items-center py-1">
          <span className="size-2.5 rounded-full border-2 border-subtle" />
          <span className="my-0.5 w-px flex-1 bg-border-strong" />
          <span className="size-2.5 rounded-full bg-primary" />
        </div>
        <div className="min-w-0 flex-1 text-sm">
          <p className="truncate">{v.origem.municipio ? `${v.origem.municipio}/${v.origem.uf}` : "—"}</p>
          <p className="mt-1.5 truncate">{v.destino.municipio ? `${v.destino.municipio}/${v.destino.uf}` : "—"}</p>
        </div>
        <div className="flex flex-col items-end justify-between text-right">
          <span className="text-[15px] font-semibold tnum">{fmt.money(v.frete)}</span>
          <span className="text-xs text-subtle tnum">{fmt.dayShort(v.data)}</span>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between border-t border-border pt-2.5">
        <ChainStatus cadeia={v.cadeia} />
        <span className="font-mono text-xs text-muted">{v.placa}</span>
      </div>
    </Card>
  );
}

const ETAPA_ICON: Record<EtapaChave, typeof FileText> = { cte: FileText, averbacao: ShieldCheck, ciot: Wallet, mdfe: Truck };

/** Cadeia da viagem em lista vertical (detalhe mobile). */
export function CadeiaVertical({ cadeia }: { cadeia: Cadeia }) {
  const etapas = Object.keys(ETAPA_NOME) as EtapaChave[];
  return (
    <ol className="rounded-xl border border-border bg-surface px-4 py-1">
      {etapas.map((k, i) => {
        const e = cadeia[k];
        const Icon = ETAPA_ICON[k];
        return (
          <li key={k} className={cn("relative flex gap-3 py-2.5", i > 0 && "border-t border-border")}>
            <StatusIcon status={e.status} className="mt-0.5 size-5" />
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-1.5 text-[15px] font-medium">
                <Icon className="size-4 text-subtle" />
                {ETAPA_NOME[k]}
                <span className="text-sm font-normal text-muted">· {STATUS_ETAPA[e.status].label}</span>
              </p>
              <p className="truncate text-[13px] text-muted">{e.detalhe}</p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** Origem → destino destacado no topo do detalhe. */
export function RotaHero({ origem, destino }: { origem: string; destino: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-[15px] font-medium">{origem}</span>
      <ArrowDown className="my-0.5 size-4 text-subtle" />
      <span className="text-[15px] font-medium">{destino}</span>
    </div>
  );
}
